import { useCallback } from 'react';
import type { VehicleByRegistrationNumberMinimal } from '@//app/api/protected/vehicle/lookup/registration-number/[registrationNumber]/minimal';
import { useLegacyTracking } from '@//providers/LegacyTrackingProvider';
import type { LegacyTrackAssignmentInitiatedEventData } from './legacy-types';

interface CreateLegacyTrackAssignmentInitiatedVariables {
  context: LegacyTrackAssignmentInitiatedEventData['context'];
  vehicle: VehicleByRegistrationNumberMinimal;
  odometerReading: {
    value: number;
    unit: 'mil' | 'km';
  };
}

export const createLegacyTrackAssignmentInitiated = (
  variables: CreateLegacyTrackAssignmentInitiatedVariables,
): LegacyTrackAssignmentInitiatedEventData => {
  const eventData: LegacyTrackAssignmentInitiatedEventData = {
    eventType: 'assignmentInitiated',
    context: variables.context,
    information: {
      drivingWheel: variables.vehicle.drivingWheel,
      enginePower: variables.vehicle.enginePower,
      equipmentLevels: variables.vehicle.equipmentLevels,
      fuelType: variables.vehicle.fuelType,
      gearboxType: variables.vehicle.gearboxType,
      manufactureYear: variables.vehicle.manufactureYear,
      manufacturer: variables.vehicle.manufacturer,
      modelName: variables.vehicle.modelName,
      modelSeries: variables.vehicle.modelSeries,
      modelYear: variables.vehicle.modelYear.toString(),
      odometerReading: variables.odometerReading,
      registrationNumber: variables.vehicle.registrationNumber,
      salesName: variables.vehicle.salesName,
      segment: variables.vehicle.segment,
      title: variables.vehicle.title,
      vinNumber: variables.vehicle.vinNumber,
    },
  };

  return eventData;
};

export const useLegacyTrackAssignmentInitiated = () => {
  const { track } = useLegacyTracking();

  const trackLegacyAssignmentInitiated = useCallback(
    (variables: CreateLegacyTrackAssignmentInitiatedVariables) => {
      // Track event with only the event-specific data
      // The provider will inject base data (source, url, eventTimestamp, userData)
      track({
        event: 'assignmentInitiated',
        data: createLegacyTrackAssignmentInitiated(variables),
      });
    },
    [track],
  );

  return {
    trackLegacyAssignmentInitiated,
  };
};
